import React, { useCallback, useEffect, useState } from "react";
import { adminApi } from "../../apiAdmin";
import { Experiment } from "./types";
import { SectionCard } from "./shared";

// ---- experiments section ----

export function ExperimentsSection() {
  const [experiments, setExperiments] = useState<Experiment[]>([]);
  const [loading, setLoading] = useState(true);
  // A failed list-load must NOT render as the "No experiments yet" empty
  // state — an admin would wrongly conclude a running experiment vanished.
  const [loadFailed, setLoadFailed] = useState(false);
  const [actionError, setActionError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [form, setForm] = useState({ name: "", description: "", variants: "control, treatment", conversionEvent: "conversion" });

  const load = useCallback(() => {
    adminApi
      .get("/experiments")
      .then((r) => {
        setExperiments(Array.isArray(r.data) ? r.data : []);
        setLoadFailed(false);
      })
      .catch(() => setLoadFailed(true))
      .finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  const create = async () => {
    setFormError("");
    const variants = form.variants
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);
    if (variants.length < 2) {
      setFormError("Enter at least 2 variants, comma-separated");
      return;
    }
    setSaving(true);
    try {
      await adminApi.post("/experiments", {
        name: form.name.trim().toLowerCase(),
        description: form.description.trim(),
        variants,
        conversionEvent: form.conversionEvent.trim() || "conversion",
      });
      setShowForm(false);
      setForm({ name: "", description: "", variants: "control, treatment", conversionEvent: "conversion" });
      load();
    } catch (e: any) {
      setFormError(e?.response?.data?.msg || "Could not create experiment");
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (exp: Experiment, status: Experiment["status"]) => {
    setActionError("");
    try {
      await adminApi.patch(`/experiments/${exp._id}/status`, { status });
    } catch (e: any) {
      setActionError(
        e?.response?.data?.msg || `Couldn't ${status === "running" ? "start" : "stop"} "${exp.name}" — try again.`
      );
    } finally {
      load(); // refetch either way so the chip always shows server truth
    }
  };

  const statusChip = (status: Experiment["status"]) => {
    const colors: Record<Experiment["status"], string> = {
      draft: "var(--adm-text-sec)",
      running: "var(--adm-green)",
      stopped: "var(--adm-orange)",
    };
    return (
      <span
        style={{
          padding: "1px 8px",
          borderRadius: 999,
          fontSize: "0.7rem",
          fontWeight: 700,
          color: "#fff",
          background: colors[status],
          textTransform: "uppercase",
        }}
      >
        {status}
      </span>
    );
  };

  return (
    <SectionCard
      title="A/B Experiments"
      note="Rails only — assignment is deterministic (same user always sees the same variant); nothing is wired into the product yet."
    >
      {actionError && (
        <p className="adm-red adm-fs-80" style={{ marginTop: 0 }}>{actionError}</p>
      )}
      {loading ? (
        <p className="muted-text">Loading experiments…</p>
      ) : loadFailed ? (
        <p className="muted-text adm-fs-82">
          Couldn’t load experiments.{" "}
          <button className="adm-select adm-pointer" onClick={load}>
            Retry
          </button>
        </p>
      ) : experiments.length === 0 && !showForm ? (
        <p className="muted-text adm-fs-82">
          No experiments yet. Create one here when you're ready to test onboarding variants at launch.
        </p>
      ) : (
        experiments.map((exp) => (
          <div
            key={exp._id}
            className="adm-p-12" style={{ border: "1px solid var(--adm-line)", borderRadius: 8, marginBottom: 10 }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <strong className="adm-fs-88">{exp.name}</strong>
              {statusChip(exp.status)}
              <span className="muted-text adm-fs-74">
                converts on “{exp.conversionEvent}”
              </span>
              <span className="adm-flex-1" />
              {exp.status !== "running" && (
                <button className="adm-select adm-pointer" onClick={() => setStatus(exp, "running")}>
                  ▶ Start
                </button>
              )}
              {exp.status === "running" && (
                <button className="adm-select adm-pointer" onClick={() => setStatus(exp, "stopped")}>
                  ⏹ Stop
                </button>
              )}
            </div>
            {exp.description && (
              <p className="muted-text adm-fs-78" style={{ margin: "6px 0 0" }}>
                {exp.description}
              </p>
            )}
            <table className="adm-table-light adm-table-compact adm-mt-8">
              <thead>
                <tr>
                  <th>Variant</th>
                  <th>Weight</th>
                  <th>Exposures</th>
                  <th>Conversions</th>
                  <th>Conversion rate</th>
                </tr>
              </thead>
              <tbody>
                {exp.results.map((r) => (
                  <tr key={r.variant}>
                    <td className="adm-semibold">{r.variant}</td>
                    <td>{r.weight}</td>
                    <td>{r.exposures}</td>
                    <td>{r.conversions}</td>
                    <td>{r.conversionPct === null ? "—" : `${r.conversionPct}%`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))
      )}

      {showForm ? (
        <div className="adm-p-12 adm-mt-8" style={{ border: "1px dashed var(--adm-line)", borderRadius: 8 }}>
          <div style={{ display: "grid", gap: 8, maxWidth: 460 }}>
            <input
              className="adm-select"
              placeholder="Name (e.g. onboarding-cta)"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <input
              className="adm-select"
              placeholder="Description (optional)"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
            <input
              className="adm-select"
              placeholder="Variants, comma-separated"
              value={form.variants}
              onChange={(e) => setForm({ ...form, variants: e.target.value })}
            />
            <input
              className="adm-select"
              placeholder="Conversion event name"
              value={form.conversionEvent}
              onChange={(e) => setForm({ ...form, conversionEvent: e.target.value })}
            />
            {formError && (
              <span className="adm-red adm-fs-78">{formError}</span>
            )}
            <div style={{ display: "flex", gap: 8 }}>
              <button className="adm-select adm-pointer adm-semibold" disabled={saving} onClick={create}>
                {saving ? "Creating…" : "Create experiment"}
              </button>
              <button className="adm-select adm-pointer" onClick={() => setShowForm(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : (
        <button
          className="adm-select adm-pointer adm-mt-4"
          onClick={() => setShowForm(true)}
        >
          + New experiment
        </button>
      )}
    </SectionCard>
  );
}
